import { TRADABLES_TYPE } from "@helpers/constants";

export const TERMS_API_BASE_URL_PATH = "terms-and-conditions/users";
export const TERMS_API_GET_BUNDLES_PATH = "/agreement-bundles";
export const TERMS_API_SAVE_AGREEMENT_PATH = "/agreements";

export interface TermsValidityOutput {
	acceptedMandatoryTerms: boolean;
	formBundles: FormBundle[];
}

export interface FormBundle {
	id: string;
	accepted: boolean;
	mandatory: boolean;
}

export interface AgreementBundle {
	type: AgreementBundleType;
	agreement_bundle_version_id: string;
	mandatory: boolean;
	documents?: AgreementBundleDocument[];
	text?: string;
	change_summary?: string;
}

export interface AgreementBundleDocument {
	id: string;
	title: string;
	type: AgreementBundleDocumentType;
	versions: AgreementBundleDocumentVersion[];
}

export interface AgreementBundleDocumentVersion {
	change_type: AgreementBundleDocumentChangeType;
	effective_at: string;
	expired_at: string;
	href: string;
	id: string;
	released_at: string;
	version: string;
}

export enum AgreementBundleDocumentChangeType {
	editorial = "EDITORIAL",
	material = "MATERIAL",
	nonMaterial = "NON_MATERIAL"
}

export enum AgreementBundleDocumentType {
	policy = "POLICY",
	security = "SECURITY",
	terms = "TERMS"
}

export enum AgreementBundleType {
	AGREEMENT = "AGREEMENT",
	NOTICE = "NOTICE"
}

export enum UserJourney {
	AdvancedTrading = "ADVANCED_TRADING",
	AppropriatenessWarning = "APPROPRIATENESS_WARNING",
	BitpandaCard = "BITPANDA_CARD",
	ChangesNotification = "CHANGES_NOTIFICATION",
	ClaimingBest = "CLAIMING_BEST",
	FiatWithdrawal = "FIAT_WITHDRAWAL",
	Onboarding = "ONBOARDING",
	VisionFeeBenefit = "VISION_FEE_BENEFIT",

	// Cash Plus
	CashPlus = "CASH_PLUS",
	CashPlusWithdrawal = "CASH_PLUS_WITHDRAWAL",
	CashPlusBalanceMove = "CASH_PLUS_BALANCE_MOVE",
	CashPlusOptOut = "CASH_PLUS_OPT_OUT",

	// Limit Orders
	TradingCryptoBuyOrder = "TRADING_CRYPTO_BUY_ORDER",
	TradingCryptoSellOrder = "TRADING_CRYPTO_SELL_ORDER",
	TradingCryptoTradeOnlyBuyOrder = "TRADING_CRYPTO_TRADE_ONLY_BUY_ORDER",

	// Savings plan
	SavingsPlanCardCryptoMetalIndex = "SAVINGS_PLAN_CARD_CRYPTO_METAL_INDEX",
	SavingsPlanCardSecurityToken = "SAVINGS_PLAN_CARD_SECURITY_TOKEN",
	SavingsPlanCardStocksEtfCommodity = "SAVINGS_PLAN_CARD_STOCKS_ETF_COMMODITY",
	SavingsPlanIbanCryptoMetalIndex = "SAVINGS_PLAN_IBAN_CRYPTO_METAL_INDEX",
	SavingsPlanIbanSecurityToken = "SAVINGS_PLAN_IBAN_SECURITY_TOKEN",
	SavingsPlanIbanStocksEtfCommodity = "SAVINGS_PLAN_IBAN_STOCKS_ETF_COMMODITY",

	// Staking
	Staking = "STAKING",
	StakingLocked = "STAKING_LOCKED",
	Unstaking = "UNSTAKING",
	UnstakingLocked = "UNSTAKING_LOCKED",
	AutoStaking = "AUTOSTAKING",
	Earning = "EARNING",
	EarningLocked = "EARNING_LOCKED",
	Releasing = "RELEASING",
	ReleasingLocked = "RELEASING_LOCKED",
	EarnOnStableCoin = "EARN_ON_STABLECOIN",
	EarnStaking = "EARN_STAKING",
	EarnStakingLocked = "EARN_STAKING_LOCKED",
	EarnUnstaking = "EARN_UNSTAKING",
	EarnUnstakingLocked = "EARN_UNSTAKING_LOCKED",

	// Buy
	TradingCommoditiesBuy = "TRADING_COMMODITIES_BUY",
	TradingBestBuy = "TRADING_BEST_BUY",
	TradingCryptoTradeOnlyBuy = "TRADING_CRYPTO_TRADE_ONLY_BUY",
	TradingCommodities = "TRADING_COMMODITIES",
	TradingCryptoBuy = "TRADING_CRYPTO_BUY",
	TradingCryptoIndexBuy = "TRADING_CRYPTO_INDEX_BUY",
	TradingEtfBuy = "TRADING_ETF_BUY",
	TradingMetalsBuy = "TRADING_METALS_BUY",
	TradingStocksBuy = "TRADING_STOCKS_BUY",
	TradingSecurityTokenBuy = "TRADING_SECURITY_TOKEN_BUY",
	TradingLeverageLongBuy = "TRADING_LEVERAGE_LONG_BUY",
	TradingLeverageShortBuy = "TRADING_LEVERAGE_SHORT_BUY",

	// Sell
	TradingCommoditiesSell = "TRADING_COMMODITIES_SELL",
	TradingCryptoIndexSell = "TRADING_CRYPTO_INDEX_SELL",
	TradingCryptoSell = "TRADING_CRYPTO_SELL",
	TradingEtfSell = "TRADING_ETF_SELL",
	TradingStocksSell = "TRADING_STOCKS_SELL",
	TradingMetalsSell = "TRADING_METALS_SELL",
	TradingLeverageLongSell = "TRADING_LEVERAGE_LONG_SELL",
	TradingLeverageShortSell = "TRADING_LEVERAGE_SHORT_SELL",
	TradingSecurityTokenSell = "TRADING_SECURITY_TOKEN_SELL",
	TradingBestSell = "TRADING_BEST_SELL",

	// Swap
	TradingCryptoCommoditiesSwap = "TRADING_CRYPTO_COMMODITIES_SWAP",
	TradingCryptoCryptoIndexSwap = "TRADING_CRYPTO_CRYPTO_INDEX_SWAP",
	TradingCryptoCryptoSwap = "TRADING_CRYPTO_CRYPTO_SWAP",
	TradingCryptoEtfSwap = "TRADING_CRYPTO_ETF_SWAP",
	TradingCryptoIndexCommoditiesSwap = "TRADING_CRYPTO_INDEX_COMMODITIES_SWAP",
	TradingCryptoIndexCryptoIndexSwap = "TRADING_CRYPTO_INDEX_CRYPTO_INDEX_SWAP",
	TradingCryptoIndexCryptoSwap = "TRADING_CRYPTO_INDEX_CRYPTO_SWAP",
	TradingCryptoIndexEtfSwap = "TRADING_CRYPTO_INDEX_ETF_SWAP",
	TradingCryptoIndexMetalsSwap = "TRADING_CRYPTO_INDEX_METALS_SWAP",
	TradingCryptoIndexStocksSwap = "TRADING_CRYPTO_INDEX_STOCKS_SWAP",
	TradingCryptoMetalsSwap = "TRADING_CRYPTO_METALS_SWAP",
	TradingCryptoStocksSwap = "TRADING_CRYPTO_STOCKS_SWAP",
	TradingEtfCommoditiesSwap = "TRADING_ETF_COMMODITIES_SWAP",
	TradingEtfCryptoIndexSwap = "TRADING_ETF_CRYPTO_INDEX_SWAP",
	TradingEtfCryptoSwap = "TRADING_ETF_CRYPTO_SWAP",
	TradingEtfEtfSwap = "TRADING_ETF_ETF_SWAP",
	TradingEtfMetalsSwap = "TRADING_ETF_METALS_SWAP",
	TradingEtfStocksSwap = "TRADING_ETF_STOCKS_SWAP",
	TradingMetalsCommoditiesSwap = "TRADING_METALS_COMMODITIES_SWAP",
	TradingMetalsCryptoIndexSwap = "TRADING_METALS_CRYPTO_INDEX_SWAP",
	TradingMetalsCryptoSwap = "TRADING_METALS_CRYPTO_SWAP",
	TradingMetalsEtfSwap = "TRADING_METALS_ETF_SWAP",
	TradingMetalsMetalsSwap = "TRADING_METALS_METALS_SWAP",
	TradingMetalsStocksSwap = "TRADING_METALS_STOCKS_SWAP",
	TradingStocksCommoditiesSwap = "TRADING_STOCKS_COMMODITIES_SWAP",
	TradingStocksCryptoIndexSwap = "TRADING_STOCKS_CRYPTO_INDEX_SWAP",
	TradingStocksCryptoSwap = "TRADING_STOCKS_CRYPTO_SWAP",
	TradingStocksEtfSwap = "TRADING_STOCKS_ETF_SWAP",
	TradingStocksMetalsSwap = "TRADING_STOCKS_METALS_SWAP",
	TradingStocksStocksSwap = "TRADING_STOCKS_STOCKS_SWAP",
	TradingCommoditiesCommoditiesSwap = "TRADING_COMMODITIES_COMMODITIES_SWAP",
	TradingCommoditiesCryptoIndexSwap = "TRADING_COMMODITIES_CRYPTO_INDEX_SWAP",
	TradingCommoditiesCryptoSwap = "TRADING_COMMODITIES_CRYPTO_SWAP",
	TradingCommoditiesEtfSwap = "TRADING_COMMODITIES_ETF_SWAP",
	TradingCommoditiesMetalsSwap = "TRADING_COMMODITIES_METALS_SWAP",
	TradingCommoditiesStocksSwap = "TRADING_COMMODITIES_STOCKS_SWAP",
	TradingEtf = "TRADING_ETF",
	TradingLeverageLong = "TRADING_LEVERAGE_LONG",
	TradingLeverageShort = "TRADING_LEVERAGE_SHORT",
	TradingMetals = "TRADING_METALS",
	TradingStocks = "TRADING_STOCKS",
	DustToBest = "DUST_TO_BEST",
	MarginTradingAdd = "MARGIN_TRADING_ADD",
	MarginTradingCore = "MARGIN_TRADING_CORE"
}

export const tradablesWithoutAppropriateness: string[] = [
	TRADABLES_TYPE.CRYPTO,
	TRADABLES_TYPE.INDEX,
	TRADABLES_TYPE.METAL
];
